import {
  HomeIcon,
  UserGroupIcon,
  CreditCardIcon,
  ChartBarIcon,
  CogIcon,
  TagIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  TruckIcon,
  CubeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const navigation: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/admin',
    icon: HomeIcon,
    description: 'Vista general del sistema',
  },
  {
    name: 'Usuarios',
    href: '/admin/users',
    icon: UserGroupIcon,
    description: 'Gestión de usuarios registrados',
  },
  {
    name: 'WebIDs y Pulseras',
    href: '/admin/webids',
    icon: CreditCardIcon,
    description: 'Administrar pulseras NFC activas',
  },
  {
    name: 'Estadísticas',
    href: '/admin/analytics',
    icon: ChartBarIcon,
    description: 'Métricas de uso y emergencias',
  },
  {
    name: 'Cupones',
    href: '/admin/coupons',
    icon: TagIcon,
    description: 'Gestión de promociones',
  },
  {
    name: 'Packs',
    href: '/admin/packs',
    icon: CurrencyDollarIcon,
    description: 'Gestión de packs de pulseras',
  },
  {
    name: 'Envíos',
    href: '/admin/shipping',
    icon: TruckIcon,
    description: 'Gestión de proveedores y servicios de envío',
  },
  {
    name: 'Paquetes',
    href: '/admin/packaging',
    icon: CubeIcon,
    description: 'Generador automático de paquetes',
  },
  {
    name: 'Seguimiento',
    href: '/admin/tracking',
    icon: MapPinIcon,
    description: 'Dashboard de seguimiento de envíos',
  },
  {
    name: 'Auditoría',
    href: '/admin/audit',
    icon: ShieldCheckIcon,
    description: 'Logs de acciones administrativas',
  },
  {
    name: 'Configuración',
    href: '/admin/settings',
    icon: CogIcon,
    description: 'Configuración del sistema',
  },
];

interface AdminSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Sidebar de navegación para el panel administrativo
 */
const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isOpen = true,
  onClose
}) => {
  const location = useLocation();

  return (
    <div className={`
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50
      w-64 bg-white shadow-lg lg:shadow-sm border-r border-gray-200 min-h-screen
      transform transition-transform duration-200 ease-in-out
    `}>
      <nav className="mt-6 px-3">
        <div className="space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/admin' && location.pathname.startsWith(item.href));
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => onClose && onClose()}
                className={`
                  group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                  ${isActive
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                title={item.description}
              >
                <item.icon
                  className={`
                    mr-3 h-5 w-5 flex-shrink-0 transition-colors duration-200
                    ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                {item.name}
              </NavLink>
            );
          })}
        </div>

        {/* Sección de acceso rápido */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Acceso Rápido
          </h3>
          <div className="mt-3 space-y-1">
            <NavLink
              to="/dashboard"
              onClick={() => onClose && onClose()}
              className="group flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
            >
              <HomeIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
              Mi Dashboard
            </NavLink>
            
            <NavLink
              to="/profile/demo"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onClose && onClose()}
              className="group flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
            >
              <CreditCardIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
              Vista Pública NFC
            </NavLink>
          </div>
        </div>

        {/* Información del sistema */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="px-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Sistema
            </h3>
            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex justify-between">
                <span>Versión:</span>
                <span className="font-mono">v0.1.0</span>
              </div>
              <div className="flex justify-between">
                <span>Entorno:</span>
                <span className="font-mono">
                  {process.env.NODE_ENV === 'development' ? 'DEV' : 'PROD'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Build:</span>
                <span className="font-mono">
                  {new Date().toLocaleDateString('es-ES')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default AdminSidebar;
